export const FEI_STATUS_OPTIONS = [
	{
		value: 'issued_all',
		label: 'All facilities already have FEI numbers issued',
		description:
			'Every manufacturing site has an active FDA Establishment Identifier with annual updates scheduled.',
	},
	{
		value: 'partial_pending',
		label: 'Some facilities have FEIs while others are pending',
		description:
			'At least one site has an FEI assigned and another location (internal or contract) is awaiting assignment.',
	},
	{
		value: 'applications_submitted',
		label: 'Applications submitted and awaiting FEI assignment',
		description:
			'Registration packets are filed, but we are waiting on the FDA (Food and Drug Administration) to issue numbers.',
	},
	{
		value: 'not_started',
		label: 'No FEI applications submitted yet',
		description:
			'We are gathering documentation and plan to initiate FEI requests in the near term.',
	},
] as const

export const FEI_STATUS_VALUE_SET = new Set(
	FEI_STATUS_OPTIONS.map((option) => option.value),
)

export const PRODUCT_CATEGORY_OPTIONS = [
	{ value: 'leave_on_skincare', label: 'Leave-on skincare' },
	{ value: 'rinse_off_skincare', label: 'Rinse-off skincare' },
	{ value: 'color_cosmetics', label: 'Color cosmetics' },
	{ value: 'hair_care', label: 'Hair care' },
	{ value: 'body_care', label: 'Body care' },
	{ value: 'fragrance', label: 'Fragrance' },
	{ value: 'sun_care', label: 'Sun care' },
	{ value: 'beauty_device', label: 'Beauty devices / tools' },
	{ value: 'other', label: 'Other' },
] as const

export const PROFESSIONAL_USE_OPTIONS = [
	{ value: 'consumer', label: 'Consumer retail' },
	{ value: 'professional_only', label: 'Professional-only' },
] as const

export const PRODUCT_STATUS_OPTIONS = [
	{ value: 'listed', label: 'Listed with FDA' },
	{ value: 'listing_in_progress', label: 'Listing in progress' },
	{ value: 'development', label: 'In development' },
	{ value: 'exempt', label: 'Not marketed in the U.S.' },
] as const

export const PRODUCT_DATA_SOURCE_OPTIONS = [
	{ value: 'unspecified', label: 'Not documented yet' },
	{
		value: 'erp_netsuite',
		label: 'ERP — NetSuite / Oracle Cloud',
	},
	{
		value: 'erp_sap',
		label: 'ERP — SAP S/4HANA or ECC',
	},
	{
		value: 'erp_d365',
		label: 'ERP — Microsoft Dynamics 365',
	},
	{
		value: 'plm',
		label: 'PLM (Centric, Arena, Propel, etc.)',
	},
	{
		value: 'cloud_database',
		label: 'Cloud database (Airtable, Smartsheet, Notion)',
	},
	{
		value: 'qms',
		label: 'QMS / eQMS (MasterControl, ETQ, Qualio)',
	},
	{
		value: 'custom_spreadsheet',
		label: 'Shared spreadsheet (Excel, Google Sheets)',
	},
	{ value: 'other', label: 'Other' },
] as const

export const DRILL_CADENCE_OPTIONS = [
	{
		value: 'monthly',
		label: 'Monthly',
		description: 'We run mock requests every month to keep custodians sharp.',
	},
	{
		value: 'quarterly',
		label: 'Quarterly',
		description: 'Once per quarter aligned to internal audit prep.',
	},
	{
		value: 'semiannual',
		label: 'Twice per year',
		description: 'Spring and fall exercises to refresh procedures.',
	},
	{
		value: 'annual',
		label: 'Annually',
		description: 'One full drill each year to validate readiness.',
	},
	{
		value: 'adhoc',
		label: 'Ad hoc when triggered (audits, escalations)',
		description: 'Exercises occur when requested by leadership or auditors.',
	},
	{
		value: 'not_established',
		label: 'Not established yet',
		description:
			'We have not defined a drill cadence yet but want to track it.',
	},
] as const

export const DRILL_CADENCE_VALUE_SET = new Set(
	DRILL_CADENCE_OPTIONS.map((option) => option.value),
)

export const TRAINING_FREQUENCY_OPTIONS = [
	{
		value: 'onboarding_only',
		label: 'Onboarding only',
		description:
			'Employees complete training once during onboarding with ad-hoc refreshers.',
	},
	{
		value: 'onboarding_annual',
		label: 'Onboarding + annual refresher',
		description:
			'Annual compliance refreshers follow the initial onboarding module.',
	},
	{
		value: 'semiannual',
		label: 'Semi-annual refresher',
		description: 'Twice-a-year training cadence for regulated roles.',
	},
	{
		value: 'quarterly',
		label: 'Quarterly refresher',
		description: 'Quarterly exercises or micro-learning for high-risk teams.',
	},
	{
		value: 'custom',
		label: 'Custom cadence — describe in the notes below',
		description:
			'You define a bespoke cadence. Capture specifics in the notes section.',
	},
] as const

export const TRAINING_FREQUENCY_VALUE_SET = new Set(
	TRAINING_FREQUENCY_OPTIONS.map((option) => option.value),
)

export const RECALL_SALES_CHANNEL_OPTIONS = [
	{ value: 'direct_to_consumer', label: 'Direct-to-consumer site' },
	{ value: 'online_marketplace', label: 'Online marketplace' },
	{ value: 'retail_partner', label: 'Retail partner' },
	{
		value: 'professional_distributor',
		label: 'Professional distributor / salon',
	},
	{ value: 'international_reseller', label: 'International reseller' },
	{ value: 'other', label: 'Other channel' },
] as const

export const RECALL_SALES_CHANNEL_VALUE_SET = new Set(
	RECALL_SALES_CHANNEL_OPTIONS.map((option) => option.value),
)

export const RECALL_DISTRIBUTION_SYSTEM_OPTIONS = [
	{ value: 'erp', label: 'ERP / MRP' },
	{ value: 'fulfillment_portal', label: '3PL or fulfillment portal' },
	{ value: 'crm', label: 'CRM / helpdesk' },
	{ value: 'spreadsheets', label: 'Spreadsheet tracker' },
	{ value: 'quality_system', label: 'Quality management system' },
	{ value: 'other', label: 'Other system' },
] as const

export const RECALL_DISTRIBUTION_SYSTEM_VALUE_SET = new Set(
	RECALL_DISTRIBUTION_SYSTEM_OPTIONS.map((option) => option.value),
)

export const RECALL_EVENT_TYPE_OPTIONS = [
	{ value: 'actual_recall', label: 'Actual recall' },
	{ value: 'market_withdrawal', label: 'Market withdrawal' },
	{ value: 'mock_recall', label: 'Mock recall drill' },
] as const

export const RECALL_EVENT_TYPE_VALUE_SET = new Set(
	RECALL_EVENT_TYPE_OPTIONS.map((option) => option.value),
)

export const RECALL_EVENT_OUTCOME_OPTIONS = [
	{ value: 'completed', label: 'Completed without further action' },
	{ value: 'completed_with_fda', label: 'Completed with FDA reporting' },
	{ value: 'mock_completed', label: 'Mock drill completed' },
	{ value: 'in_progress', label: 'In progress' },
] as const

export const RECALL_EVENT_OUTCOME_VALUE_SET = new Set(
	RECALL_EVENT_OUTCOME_OPTIONS.map((option) => option.value),
)

export const RECALL_DRILL_RECENCY_OPTIONS = [
	{ value: 'within_6_months', label: 'Within the last 6 months' },
	{ value: 'within_year', label: '6–12 months ago' },
	{ value: 'over_year', label: 'Over a year ago' },
	{ value: 'not_run', label: 'No drills completed yet' },
] as const

export const RECALL_DRILL_RECENCY_VALUE_SET = new Set(
	RECALL_DRILL_RECENCY_OPTIONS.map((option) => option.value),
)

export const DOCUMENT_LIBRARY_SYSTEM_OPTIONS = [
	{
		value: 'qms',
		label: 'Dedicated QMS (MasterControl, Veeva, etc.)',
	},
	{ value: 'sharepoint', label: 'SharePoint / OneDrive' },
	{ value: 'google_drive', label: 'Google Drive' },
	{ value: 'notion', label: 'Notion / Airtable / Coda' },
	{ value: 'file_server', label: 'On-prem file server' },
	{ value: 'lms', label: 'Learning management system (LMS)' },
	{ value: 'other', label: 'Other system' },
] as const

export const DOCUMENT_LIBRARY_SYSTEM_VALUE_SET = new Set(
	DOCUMENT_LIBRARY_SYSTEM_OPTIONS.map((option) => option.value),
)

export const DOCUMENT_SCOPE_OPTIONS = [
	{ value: 'sops', label: 'SOPs and controlled documents' },
	{ value: 'forms', label: 'Forms and templates' },
	{ value: 'training_records', label: 'Training completion records' },
	{ value: 'quality_records', label: 'Quality / deviation records' },
	{ value: 'regulatory_filings', label: 'Regulatory filings' },
	{ value: 'other', label: 'Other collection' },
] as const

export const DOCUMENT_SCOPE_VALUE_SET = new Set(
	DOCUMENT_SCOPE_OPTIONS.map((option) => option.value),
)

export const TRAINING_AUDIENCE_OPTIONS = [
	{ value: 'everyone', label: 'All employees' },
	{ value: 'manufacturing', label: 'Manufacturing / lab operators' },
	{
		value: 'regulatory_quality',
		label: 'Regulatory & Quality',
	},
	{ value: 'customer_support', label: 'Customer support' },
	{ value: 'sales_education', label: 'Sales educators / field team' },
	{ value: 'contractors', label: 'Contractors / partners' },
	{ value: 'other', label: 'Other audience' },
] as const

export const TRAINING_AUDIENCE_VALUE_SET = new Set(
	TRAINING_AUDIENCE_OPTIONS.map((option) => option.value),
)

export const TRAINING_DELIVERY_OPTIONS = [
	{ value: 'lms', label: 'Learning management system' },
	{ value: 'workshop', label: 'Live workshop or webinar' },
	{ value: 'document_signoff', label: 'Document read-and-sign' },
	{ value: 'blended', label: 'Blended (self-serve + live)' },
	{ value: 'microlearning', label: 'Microlearning / huddles' },
	{ value: 'other', label: 'Other delivery' },
] as const

export const TRAINING_DELIVERY_VALUE_SET = new Set(
	TRAINING_DELIVERY_OPTIONS.map((option) => option.value),
)

export const TRAINING_HIGHLIGHT_OPTIONS = [
	{ value: 'role_specific', label: 'Role-specific modules' },
	{ value: 'multilingual', label: 'Multilingual content' },
	{ value: 'contractor_access', label: 'Contractor onboarding' },
	{
		value: 'gmp_refreshers',
		label: 'GMP refreshers beyond compliance cadence',
	},
	{ value: 'paper_records', label: 'Paper record sign-offs' },
	{ value: 'external_partners', label: 'External partner training' },
] as const

export const TRAINING_HIGHLIGHT_VALUE_SET = new Set(
	TRAINING_HIGHLIGHT_OPTIONS.map((option) => option.value),
)

export const TRAINING_RECORD_RETENTION_OPTIONS = [
	{ value: 'three_years', label: '3 years' },
	{ value: 'four_years', label: '4 years' },
	{ value: 'five_years', label: '5 years' },
	{ value: 'six_years', label: '6 years (MoCRA minimum)' },
	{ value: 'seven_plus_years', label: '7+ years' },
] as const

export const TRAINING_RECORD_RETENTION_VALUE_SET = new Set(
	TRAINING_RECORD_RETENTION_OPTIONS.map((option) => option.value),
)

export const REGULATORY_FOCUS_OPTIONS = [
	{ value: 'federal', label: 'Federal rulemaking' },
	{ value: 'state', label: 'State legislation' },
	{ value: 'international', label: 'International developments' },
	{ value: 'retailer', label: 'Retailer requirements' },
	{ value: 'technical', label: 'Technical standards (talc, PFAS)' },
	{ value: 'other', label: 'Other focus area' },
] as const

export const REGULATORY_FOCUS_VALUE_SET = new Set(
	REGULATORY_FOCUS_OPTIONS.map((option) => option.value),
)

export const REGULATORY_FEED_OPTIONS = [
	{ value: 'fda_email', label: 'FDA email alerts' },
	{ value: 'pcpc_tracker', label: 'PCPC regulatory tracker' },
	{ value: 'consultant_briefing', label: 'Consultant briefings' },
	{ value: 'trade_association', label: 'Trade association updates' },
	{ value: 'retailer_portal', label: 'Retailer compliance portals' },
	{ value: 'legislative_monitor', label: 'Legislative monitoring tools' },
	{ value: 'news_alerts', label: 'News alerts / RSS feeds' },
	{ value: 'internal_ai', label: 'Internal AI monitoring' },
	{ value: 'other', label: 'Other feed' },
] as const

export const REGULATORY_FEED_VALUE_SET = new Set(
	REGULATORY_FEED_OPTIONS.map((option) => option.value),
)

export const REGULATORY_FEED_FREQUENCY_OPTIONS = [
	{ value: 'daily', label: 'Daily' },
	{ value: 'weekly', label: 'Weekly' },
	{ value: 'biweekly', label: 'Every two weeks' },
	{ value: 'monthly', label: 'Monthly' },
	{ value: 'quarterly', label: 'Quarterly' },
	{ value: 'as_published', label: 'As published' },
] as const

export const REGULATORY_FEED_FREQUENCY_VALUE_SET = new Set(
	REGULATORY_FEED_FREQUENCY_OPTIONS.map((option) => option.value),
)

export const CHANGE_CONTROL_STAGE_OPTIONS = [
	{ value: 'review', label: 'Review / triage' },
	{ value: 'approval', label: 'Approval decision' },
	{ value: 'implementation', label: 'Implementation coordination' },
	{ value: 'communications', label: 'Communications / training' },
	{ value: 'verification', label: 'Verification / close-out' },
] as const

export const CHANGE_CONTROL_STAGE_VALUE_SET = new Set(
	CHANGE_CONTROL_STAGE_OPTIONS.map((option) => option.value),
)

export const IMPLEMENTATION_WINDOW_OPTIONS = [
	{ value: 'within_30_days', label: 'Within 30 days' },
	{ value: 'within_60_days', label: 'Within 60 days' },
	{ value: 'within_90_days', label: 'Within 90 days' },
	{ value: 'case_by_case', label: 'Case-by-case based on risk' },
] as const

export const IMPLEMENTATION_WINDOW_VALUE_SET = new Set(
	IMPLEMENTATION_WINDOW_OPTIONS.map((option) => option.value),
)

export const IMPLEMENTATION_TRACKING_OPTIONS = [
	{ value: 'qms_change_control', label: 'QMS change-control workflow' },
	{ value: 'project_tracker', label: 'Project tracker (Asana, Monday)' },
	{ value: 'task_manager', label: 'Task manager (Jira, ClickUp)' },
	{ value: 'spreadsheet', label: 'Spreadsheet tracker' },
	{ value: 'other', label: 'Other tracking method' },
] as const

export const IMPLEMENTATION_TRACKING_VALUE_SET = new Set(
	IMPLEMENTATION_TRACKING_OPTIONS.map((option) => option.value),
)

export const REGULATORY_REVIEW_CADENCE_OPTIONS = [
	{ value: 'monthly', label: 'Monthly' },
	{ value: 'quarterly', label: 'Quarterly' },
	{ value: 'semiannual', label: 'Semi-annual' },
	{ value: 'annual', label: 'Annual' },
] as const

export const REGULATORY_REVIEW_CADENCE_VALUE_SET = new Set(
	REGULATORY_REVIEW_CADENCE_OPTIONS.map((option) => option.value),
)

export const POLICY_REFERENCE_ITEMS = [
	{
		value: 'complaint_handling_policy',
		label: 'Complaint handling policy',
		category: 'policy',
		required: true,
	},
	{
		value: 'complaint_data_privacy_policy',
		label: 'Complaint data privacy policy',
		category: 'policy',
		required: true,
	},
	{
		value: 'supplier_qualification_policy',
		label: 'Supplier qualification policy',
		category: 'policy',
		required: true,
	},
	{
		value: 'document_control_policy',
		label: 'Document control policy',
		category: 'policy',
		required: true,
	},
	{
		value: 'change_control_policy',
		label: 'Change control policy',
		category: 'policy',
		required: true,
	},
	{
		value: 'change_control_procedure',
		label: 'Change control procedure / SOP',
		category: 'procedure',
		required: true,
	},
	{
		value: 'document_control_procedure',
		label: 'Document control procedure / SOP',
		category: 'procedure',
		required: true,
	},
	{
		value: 'brand_guidelines',
		label: 'Brand / style guidelines',
		category: 'reference',
		required: false,
	},
	{
		value: 'artwork_approval_workflow',
		label: 'Artwork approval workflow',
		category: 'procedure',
		required: true,
	},
	{
		value: 'crisis_communication_plan',
		label: 'Crisis communication plan',
		category: 'plan',
		required: true,
	},
	{
		value: 'distribution_records_dataset',
		label: 'Distribution records data source',
		category: 'dataset',
		required: true,
	},
	{
		value: 'quality_agreement_template',
		label: 'Quality agreement template',
		category: 'template',
		required: true,
	},
	{
		value: 'risk_management_policy',
		label: 'Risk management policy',
		category: 'policy',
		required: true,
	},
	{
		value: 'risk_management_program',
		label: 'Risk management program / SOP',
		category: 'procedure',
		required: true,
	},
	{
		value: 'quality_manual',
		label: 'Quality manual',
		category: 'manual',
		required: true,
	},
	{
		value: 'sanitation_sop_bundle',
		label: 'Sanitation SOP bundle',
		category: 'sop',
		required: true,
	},
	{
		value: 'equipment_maintenance_sop',
		label: 'Equipment maintenance SOP',
		category: 'sop',
		required: true,
	},
	{
		value: 'deviation_capa_procedure',
		label: 'Deviation / CAPA procedure',
		category: 'procedure',
		required: true,
	},
	{
		value: 'hr_training_policy',
		label: 'HR training policy',
		category: 'policy',
		required: true,
	},
	{
		value: 'other_reference',
		label: 'Other supporting document',
		category: 'reference',
		required: false,
	},
] as const

export const POLICY_REFERENCE_VALUE_SET = new Set(
	POLICY_REFERENCE_ITEMS.map((item) => item.value),
)

export const POLICY_REFERENCE_STATUS_OPTIONS = [
	{ value: 'in_place', label: 'Completed' },
	{ value: 'in_development', label: 'In development' },
	{ value: 'needs_template', label: 'Need template' },
] as const

export const POLICY_REFERENCE_STATUS_VALUE_SET = new Set(
	POLICY_REFERENCE_STATUS_OPTIONS.map((option) => option.value),
)

export const COMPLAINT_CHANNEL_OPTIONS = [
	{ value: 'hotline', label: 'Customer service hotline' },
	{ value: 'email', label: 'Dedicated email inbox' },
	{ value: 'web_form', label: 'Website form' },
	{ value: 'retailer_portal', label: 'Retailer or marketplace portal' },
	{ value: 'social_media', label: 'Social media monitoring' },
	{ value: 'in_store', label: 'In-store / field reports' },
	{ value: 'text', label: 'SMS / messaging' },
] as const

export const COMPLAINT_CHANNEL_VALUE_SET = new Set(
	COMPLAINT_CHANNEL_OPTIONS.map((option) => option.value),
)

export const COMPLAINT_CHANNEL_OWNER_TYPE_OPTIONS = [
	{
		value: 'retailer',
		label: 'Retailer partner',
		description:
			'Sold through external retail or marketplace partners who relay complaints to you.',
	},
	{
		value: 'platform',
		label: 'Platform or tool',
		description:
			'Technology platform that hosts the channel (e.g., Shopify form, Instagram DMs).',
	},
	{
		value: 'team',
		label: 'Internal team',
		description:
			'In-house staff operating the channel (e.g., Consumer Care, Quality Ops).',
	},
	{
		value: 'other',
		label: 'Other owner',
		description:
			'Agency, distributor, or an arrangement that does not fit above.',
	},
] as const

export const COMPLAINT_CHANNEL_OWNER_TYPE_VALUE_SET = new Set(
	COMPLAINT_CHANNEL_OWNER_TYPE_OPTIONS.map((option) => option.value),
)

export const COMPLAINT_SYSTEM_PLATFORM_OPTIONS = [
	{ value: 'zendesk', label: 'Zendesk' },
	{ value: 'salesforce', label: 'Salesforce Service Cloud' },
	{ value: 'freshdesk', label: 'Freshdesk' },
	{ value: 'hubspot', label: 'HubSpot Service Hub' },
	{ value: 'airtable', label: 'Airtable / database' },
	{ value: 'spreadsheet', label: 'Spreadsheet tracker' },
	{ value: 'custom', label: 'Custom / in-house system' },
	{ value: 'other', label: 'Other platform' },
] as const

export const COMPLAINT_SYSTEM_PLATFORM_VALUE_SET = new Set(
	COMPLAINT_SYSTEM_PLATFORM_OPTIONS.map((option) => option.value),
)

export const COMPLAINT_SYSTEM_USAGE_OPTIONS = [
	{ value: 'intake', label: 'Intake / triage' },
	{ value: 'case_management', label: 'Case management' },
	{ value: 'master_log', label: 'Master complaint log' },
	{ value: 'analytics', label: 'Analytics / trending' },
] as const

export const COMPLAINT_SYSTEM_USAGE_VALUE_SET = new Set(
	COMPLAINT_SYSTEM_USAGE_OPTIONS.map((option) => option.value),
)

export const CHANNEL_SYSTEM_FALLBACK_OPTIONS = [
	{
		value: 'manual_spreadsheet',
		label: 'Manual spreadsheet / shared tracker',
		description:
			'Complaints are copied into a spreadsheet or shared workbook instead of a helpdesk.',
	},
	{
		value: 'no_system_yet',
		label: 'Not logged yet (manual hand-off)',
		description:
			'The team triages complaints manually without a defined system.',
	},
] as const

export const ACKNOWLEDGEMENT_WINDOW_OPTIONS = [
	{ value: 'same_day', label: 'Same day' },
	{ value: 'within_1_business_day', label: 'Within 1 business day' },
	{ value: 'within_2_business_days', label: 'Within 2 business days' },
	{ value: 'within_3_business_days', label: 'Within 3 business days' },
	{ value: 'custom', label: 'Custom window' },
] as const

export const ACKNOWLEDGEMENT_WINDOW_VALUE_SET = new Set(
	ACKNOWLEDGEMENT_WINDOW_OPTIONS.map((option) => option.value),
)

export const ACKNOWLEDGEMENT_CHANNEL_OPTIONS = [
	{ value: 'email', label: 'Email response' },
	{ value: 'phone', label: 'Phone call' },
	{ value: 'portal', label: 'Retailer portal message' },
	{ value: 'text', label: 'SMS / messaging' },
	{ value: 'auto_reply', label: 'Automated acknowledgement' },
	{ value: 'other', label: 'Other channel' },
] as const

export const ACKNOWLEDGEMENT_CHANNEL_VALUE_SET = new Set(
	ACKNOWLEDGEMENT_CHANNEL_OPTIONS.map((option) => option.value),
)

export const ESCALATION_STAGE_OPTIONS = [
	{ value: 'serious_event', label: 'Serious adverse events' },
	{ value: 'every_case', label: 'Every complaint' },
	{ value: 'trend_alert', label: 'Trend alerts / spikes' },
] as const

export const ESCALATION_STAGE_VALUE_SET = new Set(
	ESCALATION_STAGE_OPTIONS.map((option) => option.value),
)
